import { Badge } from "@/components/common/Badge";
import { Button } from "@/components/common/Button";
import { AuditLog } from "@/contracts/auth-api.types";
import { useNotification } from "@/hooks/useNotification";
import { auditLogService } from "@/services/auditLogService";
import { formatDate } from "@/utils/formatters";
import { ChevronLeft, ChevronRight, Eye, FileText, RefreshCw, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { AuditMetadataModal } from "./AuditMetadataModal";

export const AuditLogsList: React.FC = () => {
  const { notify } = useNotification();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [totalLogs, setTotalLogs] = useState(0);
  const [page, setPage] = useState(1);
  const [userNameFilter, setUserNameFilter] = useState("");
  const [userIdFilter, setUserIdFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  const limit = 15;

  const loadLogs = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await auditLogService.list({
        page,
        limit,
        user_name: userNameFilter.trim() || undefined,
        user_id: userIdFilter.trim() || undefined,
      });

      if (Array.isArray(response)) {
        setLogs(response);
        setTotalLogs(response.length);
      } else {
        setLogs(response.data || []);
        setTotalLogs(response.total || 0);
      }
    } catch (err: any) {
      notify(err.message || "Erro ao carregar logs de auditoria", "error");
    } finally {
      setIsLoading(false);
    }
  }, [page, userNameFilter, userIdFilter, notify]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const totalPages = Math.max(1, Math.ceil(totalLogs / limit));

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadLogs();
  };

  const handleClearFilters = () => {
    setUserNameFilter("");
    setUserIdFilter("");
    setPage(1);
  };

  const getActionVariant = (
    action: string,
  ): "success" | "danger" | "warning" | "info" | "neutral" => {
    const act = action.toUpperCase();
    if (act.includes("CREATE") || act.includes("POST") || act.includes("REGISTER"))
      return "success";
    if (act.includes("DELETE") || act.includes("REMOVE") || act.includes("REVOKE")) return "danger";
    if (
      act.includes("UPDATE") ||
      act.includes("PUT") ||
      act.includes("PATCH") ||
      act.includes("CHANGE")
    )
      return "warning";
    if (act.includes("LOGIN") || act.includes("AUTH") || act.includes("SESSION")) return "info";
    return "neutral";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Trilha de Auditoria</h1>
          <p className="text-muted text-sm mt-0.5">
            Registro imutável de ações administrativas, alterações de estado e eventos de segurança.
          </p>
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={loadLogs}
          isLoading={isLoading}
          title="Recarregar logs"
        >
          <RefreshCw size={16} />
          <span className="hide-sm">Recarregar</span>
        </Button>
      </div>

      {/* Filter and Search Bar */}
      <div className="card p-4">
        <form onSubmit={handleSearchSubmit} className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Filtrar por nome de usuário..."
              value={userNameFilter}
              onChange={(e) => setUserNameFilter(e.target.value)}
              className="input-control text-sm py-2"
            />
          </div>

          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder="Filtrar por User ID (UUID)..."
              value={userIdFilter}
              onChange={(e) => setUserIdFilter(e.target.value)}
              className="input-control text-sm py-2 font-mono"
            />
          </div>

          <Button type="submit" variant="primary" size="sm">
            <Search size={16} />
            <span>Filtrar</span>
          </Button>

          {(userNameFilter || userIdFilter) && (
            <Button type="button" variant="ghost" size="sm" onClick={handleClearFilters}>
              Limpar
            </Button>
          )}
        </form>
      </div>

      {/* Logs Table */}
      <div className="card p-0 overflow-hidden">
        {isLoading ? (
          <div className="py-12 text-center">
            <div className="spinner mx-auto mb-3"></div>
            <p className="text-sm text-muted">Consultando trilha de auditoria...</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="py-12 text-center text-muted">
            <FileText size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-base font-medium">Nenhum evento registrado com os filtros atuais.</p>
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table">
              <thead>
                <tr>
                  <th>Ação</th>
                  <th>Usuário</th>
                  <th>User ID</th>
                  <th>Data/Hora</th>
                  <th className="text-right">Metadados</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id}>
                    <td>
                      <Badge variant={getActionVariant(log.action)} className="font-mono text-xs">
                        {log.action}
                      </Badge>
                    </td>
                    <td className="font-medium text-sm text-gray-900">{log.user_name}</td>
                    <td className="text-xs font-mono text-muted truncate max-w-[150px]">
                      {log.user_id || "Sistema"}
                    </td>
                    <td className="text-xs text-muted">{formatDate(log.timestamp)}</td>
                    <td className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedLog(log)}
                        title="Inspecionar metadados JSON"
                        className="py-1 px-2"
                      >
                        <Eye size={15} />
                        <span className="text-xs ml-1">Detalhes</span>
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination controls */}
        {!isLoading && totalLogs > 0 && (
          <div className="p-4 border-t border-gray-100 flex justify-between items-center flex-wrap gap-2 text-xs text-muted">
            <span>
              Mostrando página <strong>{page}</strong> de <strong>{totalPages}</strong> (Total:{" "}
              {totalLogs} eventos)
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft size={16} />
                <span>Anterior</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <span>Próxima</span>
                <ChevronRight size={16} />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Metadata Inspector Modal */}
      <AuditMetadataModal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        log={selectedLog}
      />
    </div>
  );
};
